import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { Reveal, FadeUp } from "@/components/site/Reveal";
import { useGuides, useGuideCategories, resolveImg } from "@/hooks/usePublicContent";

export default function GuideIndex() {
  const { items: guides, ready } = useGuides();
  const categories = useGuideCategories();
  const byCat = (slug) => guides.filter((g) => g.category === slug);
  const hasArticles = guides.length > 0;

  return (
    <SmoothScroll>
      <Seo
        pageKey="home"
        overrideTitle="Perth Tiling Guide — Costs, Tile Selection & How-Tos | Perth Tiling WA"
        overrideDescription="Practical, honest advice on tiling in Perth — costs, tile selection, large-format, natural stone, waterproofing and renovation planning from Perth Tiling WA."
        overrideKeywords="tiling guide Perth, tiling costs, tile selection, large format tiles, bathroom tiling advice"
        noindex={!hasArticles}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Tiling Guide", path: "/guide" }]}
      />
      <div className="grain-overlay" aria-hidden />
      <div data-testid="guide-index" className="bg-[#f8fafc] min-h-screen">
        <Navbar />

        <section className="pt-40 pb-16 md:pb-24">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <p className="overline !text-[#9c7a4d] mb-6 flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> Perth Tiling Guide</p>
            <h1 className="font-serif font-light text-[#0f172a] text-4xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-4xl">
              <Reveal load>Advice worth tiling around.</Reveal>
            </h1>
            <p className="mt-8 text-[#475569] text-lg md:text-xl font-light leading-relaxed max-w-2xl">
              Honest, practical guidance from Perth's tiling specialists — from what tiling really costs to choosing
              the right tile, working with natural stone and getting waterproofing right.
            </p>
          </div>
        </section>

        {/* Categories */}
        <section className="pb-8">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {categories.map((c, i) => {
              const count = byCat(c.slug).length;
              return (
                <FadeUp key={c.slug} delay={(i % 3) * 0.05}>
                  <div data-testid={`guide-category-${c.slug}`} className="h-full border border-[#0f172a]/10 bg-white p-7 flex flex-col">
                    <h2 className="font-serif text-2xl text-[#0f172a] font-light mb-2">{c.title}</h2>
                    <p className="text-[#64748b] font-light text-sm leading-relaxed flex-1">{c.description}</p>
                    <p className="mt-4 overline !text-[#94a3b8]">{count} article{count === 1 ? "" : "s"}</p>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </section>

        {/* Published articles */}
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <h2 className="font-serif text-3xl md:text-4xl text-[#0f172a] font-light mb-10">Latest articles</h2>
            {!ready ? null : hasArticles ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {guides.map((g, i) => (
                  <motion.div key={g.id} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-6%" }} transition={{ duration: 0.6, delay: (i % 3) * 0.05 }}>
                    <Link to={`/guide/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="group block border border-[#0f172a]/10 bg-white overflow-hidden hover:shadow-[0_24px_60px_-30px_rgba(33,29,24,0.4)] transition-shadow">
                      {g.cover_image ? (
                        <div className="relative aspect-[16/10] overflow-hidden">
                          <img src={resolveImg(g.cover_image)} alt={g.cover_alt || g.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.1s] group-hover:scale-105" />
                        </div>
                      ) : null}
                      <div className="p-6">
                        <p className="overline !text-[#9c7a4d] mb-2">{(categories.find((c) => c.slug === g.category) || {}).title || g.category}</p>
                        <h3 className="font-serif text-xl text-[#0f172a] font-light leading-tight mb-2">{g.title}</h3>
                        <p className="text-[#64748b] font-light text-sm leading-relaxed line-clamp-2">{g.excerpt}</p>
                        <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#9c7a4d]">Read <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" /></span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p data-testid="guide-empty" className="text-[#64748b] font-light text-lg">New guides are on the way — check back soon.</p>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
